'use client';

import clsx from 'clsx';
import { Lock } from 'lucide-react';
import { FIELD_ERROR_CLASS, FieldErrorText } from './FieldErrorText';
import {
  Children,
  cloneElement,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

/**
 * BL-1087 — the set of field names the form's Zod schema will reject when left
 * blank, supplied by the form and consumed by every `FormField` beneath it.
 *
 * Lets the primitive source requiredness from the same schema the resolver runs
 * against, so the marker and the validation rule stop being two independent
 * decisions that drift apart (BUG-1064, one layer up). Admin forms build the
 * set with `deriveRequiredFields` from
 * `@auctogon/core-admin/forms/required-fields`; `packages/ui` stays free of a
 * Zod dependency by taking the finished set.
 *
 * `null` (the default) means no form is providing one — `FormField` then falls
 * back to its `required` prop exactly as before.
 */
export const RequiredFieldsContext = createContext<ReadonlySet<string> | null>(
  null,
);

/**
 * BL-1978 — how the fields beneath a form lay out.
 *
 * `stacked` (the default) is the label-above-control shape every form has used.
 * `row` is the read-first drawer's shape: a fixed label column beside the
 * control, each row 36px tall on desktop and 44px on a phone (PRJ-77, Dan Saper
 * 2026-09-26). The form chooses once, by providing the context, so every field
 * under it — `FormField`, `FieldRow`, and the admin widgets built on them —
 * follows without a per-call prop.
 */
export type FieldLayout = 'stacked' | 'row';

/** The layout every `FormField` / `FieldRow` beneath it renders in. */
export const FieldLayoutContext = createContext<FieldLayout>('stacked');

/**
 * The field geometry a form's controls render at (BL-2108). `touch` is the
 * phone-sheet field (BUG-1860): 48px with 16px text, so every full-size control
 * in a full-screen sheet shares one height without each caller threading a
 * `size` prop through its own wrappers. `undefined` (the default) changes
 * nothing. The admin `Modal` provides it while it is presented as a phone
 * sheet; a compact (`sm`) control keeps its own size.
 */
export type FieldSize = 'touch' | undefined;

/** The field size every full-size control beneath it renders at. */
export const FieldSizeContext = createContext<FieldSize>(undefined);

/**
 * BUG-2653 — the suffix that keeps one form's element ids unique when a second
 * instance of it shares the document (the docked lot panel and the Add lot
 * dialog are two lot forms at once). A form provides it once; every `FormField`
 * beneath it appends it to the field's DOM ids (control, label, error, helper)
 * while `name` stays the schema key. `''` (the default) leaves ids as `name`.
 */
export const FieldIdScopeContext = createContext<string>('');

/**
 * Append a form's id scope to a field's base id.
 *
 * @param scope - the scope a form provides through {@link FieldIdScopeContext}
 * @param id - the field's unscoped id or name
 * @returns `<id>-<scope>`, or `id` when there is no scope
 */
export function scopeFieldId(scope: string, id: string): string {
  return scope ? `${id}-${scope}` : id;
}

/**
 * A field's base id scoped to the form it sits in (BUG-2653). For a control a
 * `FormField` can't wrap that still needs an id of its own.
 *
 * @param id - the field's unscoped id or name
 * @returns the id to put on the element, unique per form instance
 */
export function useScopedFieldId(id: string): string {
  return scopeFieldId(useContext(FieldIdScopeContext), id);
}

/**
 * BUG-2334 — the label a `FormField` draws and the id of the control it labels.
 * A control that names itself some other way (a combobox's own value, a rich
 * editor, a group of inputs) reads this to be named by the visible label
 * instead. `null` outside a `FormField`.
 */
export interface FieldLabelTarget {
  /** The `<label>`'s id, for `aria-labelledby`. */
  labelId: string;
  /** The id FormField hands the control, which the `<label for>` points at. */
  controlId: string;
}

const FieldLabelTargetContext = createContext<FieldLabelTarget | null>(null);

/**
 * The label of the `FormField` this control sits in (BUG-2334).
 *
 * @returns the label target, or `null` when the control is not inside a FormField
 */
export function useFieldLabelTarget(): FieldLabelTarget | null {
  return useContext(FieldLabelTargetContext);
}

/**
 * BL-2548 — a phone-sheet field's resting border: #e5e5e5 (Tailwind `neutral-200`, the
 * picked card's edge). #e5e5e5 on white is 1.26:1 by `scripts/contrast.mjs`, below the 3:1
 * non-text floor. ADR 051 — Contrast Floor's sheet-field amendment exempts it: at rest the
 * field is identified by its 48px box and its label, its focus border is dark teal (`--teal-800`,
 * 9.15:1, BUG-2057) and its error border stays red-600 (4.83:1). Only the resting border
 * uses it. Error, focus and disabled keep their own, and desktop keeps gray-300.
 */
export const SHEET_FIELD_BORDER = 'border-neutral-200';

/**
 * BL-2547 — the label column of a phone-sheet `row`. `compact` (88px) is for a
 * group whose labels are all short and whose values need the width, such as the
 * lot's Equipment group, where 124px cut the VIN example off. `default` keeps the
 * board's 124px. It changes nothing off the sheet (`FieldSizeContext` not `touch`).
 */
export type FieldRowColumn = 'default' | 'compact';

/** The label column every touch `row` beneath it uses. */
export const FieldRowColumnContext = createContext<FieldRowColumn>('default');

/**
 * Row-layout grid: a 116px label column (the Variant C mock's measured label
 * width) beside a fluid control column. `min-h-11` / `sm:min-h-9` are the
 * 44px phone / 36px desktop row heights.
 */
const ROW_GRID_CLASS =
  'grid grid-cols-[116px_minmax(0,1fr)] items-start gap-x-3';
const ROW_CELL_CLASS = 'flex min-h-11 items-center sm:min-h-9';
const ROW_LABEL_CLASS = 'flex min-h-11 items-center gap-1.5 text-body text-gray-600 sm:min-h-9';

/**
 * BL-2111 — the same row inside a phone sheet (`FieldSizeContext` = `touch`).
 * The board's measurements: a 124px label column (a 16px label needs the extra
 * 8px over 116), 48px rows with 4px above and below (8px between two rows), and
 * the label at 16px (weight 400 since BL-2620). No `sm:` resets: a sheet only exists below 768px.
 */
const ROW_GRID_CLASS_TOUCH =
  'grid grid-cols-[124px_minmax(0,1fr)] items-start gap-x-3 py-1';
/** The touch row with the compact 88px label column (BL-2547). */
const ROW_GRID_CLASS_TOUCH_COMPACT =
  'grid grid-cols-[88px_minmax(0,1fr)] items-start gap-x-3 py-1';
const ROW_CELL_CLASS_TOUCH = 'flex min-h-12 items-center';
// BL-2620 (Vadim, 2026-10-20): the row label is 16px at weight 400 in gray-600 (#4B5563,
// 7.56:1 on white), told apart from its 16/500 value by weight and colour. Only headings are
// 600 (ADR 063 — Type Scale).
const ROW_LABEL_CLASS_TOUCH = 'flex min-h-12 items-center gap-1.5 text-body-lg font-normal text-gray-600';
/**
 * BL-2759 — the two stacked labels split into look and margin, so the padlock
 * row can take the margin while the label inside it takes only the look.
 */
const STACKED_LABEL_PARTS = {
  touch: { look: 'block text-body-lg font-medium text-gray-800', margin: 'mb-1.5' },
  // BL-2916: the admin form style, 14/20 · 500 in ink-900 (ADR 063 — Type Scale). FormField is admin-only.
  card: { look: 'block text-body font-medium text-[var(--ink-900)]', margin: 'mb-1' },
} as const;

/**
 * The stacked label above a control in a phone sheet: 16px / 500 in gray-800
 * (#1F2937, 14.68:1 on white), 6px above the control (ADR 063 — Type Scale
 * Amendment 5, BL-2402). Exported so AddressFormFields reuses it (BL-2754).
 */
export const STACKED_LABEL_CLASS_TOUCH = clsx(STACKED_LABEL_PARTS.touch.look, STACKED_LABEL_PARTS.touch.margin);
/**
 * The admin field label off the sheet: 14/20 · 500 in ink-900, 4px above its control (BL-2916).
 * BL-2930: every admin field label drawn outside FormField reads this, so there is one label style.
 */
export const FIELD_LABEL_CLASS = clsx(STACKED_LABEL_PARTS.card.look, STACKED_LABEL_PARTS.card.margin);
/** {@link FIELD_LABEL_CLASS} without its margin, for a label whose parent spaces it (a row with a padlock or a button). */
export const FIELD_LABEL_LOOK = STACKED_LABEL_PARTS.card.look;
/**
 * A caption over a column: the admin table column header (BL-2935) and a tier editor's column
 * caption (BL-2931). 14/20 · 500 in ink-900 (#111827: 17.74:1 on white, 16.68:1 on the
 * `.ui-table` head's --surface-paper), sentence case. settings-v2.css `.ui-table thead th` sets
 * the same look for DataTableV2; `table-header-role.test.ts` pins the two together.
 */
export const COLUMN_CAPTION_CLASS = 'text-body font-medium text-[var(--ink-900)]';

/** The padlock beside a locked field's label (ADR 034 — Field Editability), in both layouts. */
function FieldLockIcon({ htmlFor, testField }: { htmlFor?: string; testField?: string }): ReactElement {
  // The automation handle names the field, not the form instance: drop the id scope (BUG-2653).
  const scope = useContext(FieldIdScopeContext);
  const suffix = `-${scope}`;
  const field = testField ?? (htmlFor && scope && htmlFor.endsWith(suffix) ? htmlFor.slice(0, -suffix.length) : htmlFor);
  return (
    <Lock
      className="h-3.5 w-3.5 shrink-0 text-gray-500"
      aria-hidden="true"
      data-testid={field ? `${field}-lock-icon` : undefined}
    />
  );
}

// ---------------------------------------------------------------------------
// BL-1087 — the single place the required marker is drawn.
//
// Co-located with FormField rather than split into its own module: it is the
// label half of this same form-field pattern, not an independently useful
// primitive, and `web.ts` already groups the pattern's pieces here. Before
// this, the markup existed in four private copies (FormField's inline label,
// AddressFormFields' local FieldLabel, PhoneInput's PhoneInputLabel, and
// admin-web's SearchableSelect), which is how the marker drifted to two
// different reds. Per ADR 030 the behaviour lives in one place and per-app
// looks arrive as class names — the same way the dialect system already feeds
// PhoneInput.
// ---------------------------------------------------------------------------

/** Default marker styling. Callers pass `markClassName` to bind a dialect. */
const DEFAULT_MARK_CLASS = 'text-red-600 ml-0.5';
/** The row layout's marker: beside the one-line label, never squeezed out by it. */
const ROW_MARK_CLASS = 'text-[var(--danger-500)] ml-0.5 shrink-0';
/** The admin field's required marker: the error's red, so the asterisk and the message read as one (BL-2916). */
export const FIELD_MARK_CLASS = 'text-[var(--danger-500)] ml-0.5';

/**
 * The required asterisk on its own, for the cases a whole `FieldLabel` doesn't
 * fit — a label that also wraps its input (implicit association), or one where
 * the marker is a branch of a ternary against some other hint. Same decorative,
 * `aria-hidden` treatment; requiredness reaches assistive tech from the input's
 * own validation.
 *
 * Prefer {@link FieldLabel}. This exists so those two shapes still share one
 * marker implementation instead of re-inlining the span.
 *
 * @param props - optional class override to bind a per-app dialect
 * @returns the marker span
 */
export function RequiredMarker({ className }: { className?: string }): ReactElement {
  return (
    <span className={className ?? DEFAULT_MARK_CLASS} aria-hidden="true">
      *
    </span>
  );
}

export interface FieldLabelProps {
    /** Visible label text. Renders nothing when absent. */
    label?: string;
    /** `for` attribute — should match the input's `id`. */
    htmlFor?: string;
    /** The label's own id, for a control named by `aria-labelledby` (BL-1978). */
    id?: string;
    /** Whether to draw the required marker. */
    required?: boolean;
    /** Classes for the `<label>` element itself. */
    className?: string;
    /** Classes for the marker `<span>`. Defaults to {@link DEFAULT_MARK_CLASS}. */
    markClassName?: string;
    /** Hover text — the full label where the row layout cuts it to one line. */
    title?: string;
}

/**
 * A field's `<label>` plus its required marker.
 *
 * The marker is `aria-hidden` on purpose: requiredness is announced to screen
 * readers from the input's own `required` / `aria-required`, so reading a bare
 * "star" after the label would be noise.
 *
 * @param props - label text, target id, required flag, and optional dialect classes
 * @returns the rendered label, or `null` when there is no label text
 */
export function FieldLabel({
    label,
    htmlFor,
    id,
    required,
    className,
    markClassName,
    title,
}: FieldLabelProps): ReactElement | null {
    if (!label) return null;

    return (
        <label htmlFor={htmlFor} id={id} className={className} title={title}>
            {label}
            {required ? <RequiredMarker className={markClassName} /> : null}
        </label>
    );
}

export interface FormFieldProps {
  label: string;
  name: string;
  error?: string;
  helperText?: string;
  /**
   * Force the required marker on or off. Omit it to let
   * {@link RequiredFieldsContext} decide — an explicit value always wins, so
   * existing call sites keep their current behaviour.
   */
  required?: boolean;
  className?: string;
  /**
   * Visually hide the label while keeping it in the accessibility tree
   * (sr-only). Use when a shared visible header already labels the field —
   * e.g. a repeating tier grid with column headers — so screen readers still
   * announce each input without duplicating the visible text. The inline error
   * still renders below the input.
   */
  hideLabel?: boolean;
  /**
   * Disable the wrapped input (BL-1068 / ADR 034 — field-editability locks).
   * When `true`, FormField forwards `disabled` + `aria-disabled` onto the child
   * so the field can't be edited, and — via {@link disabledHint} — explains why.
   * `undefined` leaves the child's own `disabled` untouched.
   */
  disabled?: boolean;
  /**
   * Plain-English reason the field is locked (e.g. "This lot is sold — this
   * field can't be changed."). Rides the helperText slot when the field is
   * disabled and there is no error to show (error always wins).
   */
  disabledHint?: string;
  /**
   * Draw the row layout's lock icon (ADR 034 — Field Editability). Pass the
   * caller's lock verdict when the field can be disabled for another reason
   * too, e.g. a price waiting on its currency (BUG-2025). Omitted, a disabled
   * row reads as locked. Omitted, a stacked label reads as locked only when
   * the field is disabled with a {@link disabledHint}, so a plain disabled
   * field keeps its look (BL-2759, BUG-2545).
   */
  locked?: boolean;
  /**
   * BL-2398 (ADR 030 — Design Canon, as amended): when the {@link disabledHint}
   * shows. `'always'` (the default) shows it under the field at once.
   * `'on-attempt'` keeps it in the accessibility tree but visually hidden until
   * the operator presses or focuses the locked control (see
   * {@link useAttemptReveal}). A variant, never a domain value.
   */
  disabledHintReveal?: 'always' | 'on-attempt';
  /**
   * BL-2398: the helper line's tone. `'caution'` is a field-editability caution
   * (ADR 034 — Field Editability's warn tier): warning ink plus `data-caution`.
   * The lock hint is never cautioned — it keeps the muted tone.
   */
  helperTone?: 'muted' | 'caution';
  /**
   * Exactly one React element. FormField injects `id`, `invalid`,
   * `aria-invalid`, `aria-describedby`, and (when {@link disabled}) `disabled` +
   * `aria-disabled` via `cloneElement`. Pass multiple children as siblings
   * outside the FormField wrapper — the runtime `Children.only` guard throws on
   * arrays / fragments / multiple elements.
   *
   * `invalid` is the ONE injected prop a raw DOM child does not receive — see
   * {@link isHostElement}.
   */
  children: ReactElement;
}

/**
 * Whether a child is a raw DOM element (`<input>`, `<textarea>`, `<select>`)
 * rather than a React component. React represents a host element's `type` as
 * the tag string; a component's is a function or object.
 *
 * FormField's `invalid` prop is an ADR 016 *component* convention — the design-
 * system primitives (`TextInput`, `NumberInput`, `TextArea`, `Select`, …) read
 * it and map it onto `aria-invalid` plus the red border. A host element has no
 * such prop, so React tries to write it as an HTML attribute, finds no
 * `invalid` attribute, and logs
 * "Received `false` for a non-boolean attribute `invalid`" on every render —
 * once per raw-input child, on page load, before any interaction (BUG-1344;
 * surfaced through the lienholder modal, which an eager modal then mounted
 * on the customer detail page). Withholding it costs nothing: the accessible
 * signal rides `aria-invalid`, which IS valid on a host element and is injected
 * for every child regardless.
 *
 * @param type - the child element's `type` field
 * @returns true when the child is a raw DOM element
 */
function isHostElement(type: ReactElement['type']): boolean {
  return typeof type === 'string';
}

/**
 * BL-2398 — the caution line's ink.
 * Contrast (contrast-floor.md): amber-700 #b45309 on white #ffffff — 5.02:1 text ✓ (needs 4.5).
 * A Tailwind palette class, not an admin `--warning-*` var, because bidder-web
 * shares this module and defines no such var.
 */
const CAUTION_TEXT_CLASS = 'text-amber-700';
/** The hint's one muted colour, ink-500 (#6B7280, 4.83:1 on white; ADR 051 — Contrast Floor), in both layouts (BL-2916). */
const FIELD_HINT_COLOR = 'text-[var(--ink-500)]';

/** Inputs to {@link useAttemptReveal}. */
export interface AttemptRevealOptions {
  /** The control is locked AND has a reason to show. Never bare `disabled`. */
  locked: boolean;
  /** The id of the line carrying the reason, so focus announces it. */
  hintId?: string;
  /** Automation namespace: `<testId>-lock-reveal` / `<testId>-lock-overlay`. */
  testId: string;
}

/** What {@link useAttemptReveal} hands back. */
export interface AttemptReveal {
  /** True once the operator has pressed or focused the locked control. */
  revealed: boolean;
  /** Wraps the control: a focusable group plus a press-catching overlay while locked. */
  wrap: (control: ReactNode) => ReactNode;
}

/**
 * BL-2398 — reveal a locked field's reason when the operator tries the control.
 *
 * A disabled control receives no clicks and no focus, so while `locked` the
 * control sits in a relative wrapper that is itself the attempt target: a
 * transparent overlay catches the press, and the wrapper takes the Tab stop the
 * disabled control gave up (a `role="group"` named "Locked" and described by
 * the reason — not named after the field, so the field's own label still names
 * exactly one control). Focus leaving the group hides the reason again.
 * Unlocked, `wrap` returns the control untouched.
 * The one implementation every lot-panel lock row shares (ADR 044 — Must
 * Consolidate). The reason line itself stays the caller's.
 *
 * @param options - {@link AttemptRevealOptions}
 * @returns whether the reason is revealed, and the wrapper to put the control in
 */
export function useAttemptReveal({ locked, hintId, testId }: AttemptRevealOptions): AttemptReveal {
  const [attempted, setAttempted] = useState(false);
  const reveal = useCallback(() => setAttempted(true), []);
  // Moving on (Tab away, clicking Edit, another field) hides it again, so a
  // reason revealed in view mode doesn't carry into Edit.
  const conceal = useCallback(() => setAttempted(false), []);
  const wrap = useCallback(
    (control: ReactNode) =>
      locked ? (
        <div
          role="group"
          tabIndex={0}
          aria-label="Locked"
          aria-describedby={hintId}
          onFocus={reveal}
          onBlur={conceal}
          data-testid={`${testId}-lock-reveal`}
          className="relative rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        >
          {control}
          <div
            aria-hidden="true"
            onPointerDown={reveal}
            data-testid={`${testId}-lock-overlay`}
            className="absolute inset-0 cursor-not-allowed"
          />
        </div>
      ) : (
        control
      ),
    [locked, hintId, testId, reveal, conceal],
  );
  return { revealed: locked && attempted, wrap };
}

type InjectedInputProps = {
  id?: string;
  invalid?: boolean;
  'aria-invalid'?: boolean | undefined;
  'aria-describedby'?: string;
  disabled?: boolean;
  'aria-disabled'?: boolean | undefined;
};

/** A react-hook-form `Controller`-shaped child: it renders the real control through `render`. */
type RenderPropChild = { render?: (...args: never[]) => ReactNode };

/**
 * BUG-2334 — hand a FormField's id and aria wiring to the control that will
 * actually render. A direct child gets them as props. A `Controller` (a `render`
 * prop child) discards unknown props, so they are applied to the element its
 * `render` returns instead; otherwise `<label for>` points at nothing.
 *
 * @param child - the FormField's only child
 * @param injected - the id, aria and disabled props to apply
 * @returns the child to render
 */
function injectFieldProps(
  child: ReactElement<InjectedInputProps & RenderPropChild>,
  injected: InjectedInputProps,
): ReactElement {
  const render = child.props.render;
  if (typeof render !== 'function') return cloneElement(child, injected);
  const wrapped = (...args: never[]): ReactNode => {
    const rendered = render(...args);
    // Only a component or labelable control takes the wiring; a wrapper div
    // (rich editors) keeps its own markup.
    if (!isValidElement<InjectedInputProps>(rendered)) return rendered;
    if (typeof rendered.type === 'string' && !['input', 'textarea', 'select'].includes(rendered.type)) return rendered;
    const own = rendered.props;
    return cloneElement(rendered, {
      ...injected,
      // The render's own flags win: it may read RHF's fieldState itself.
      invalid: own.invalid ?? injected.invalid,
      'aria-invalid': own['aria-invalid'] ?? injected['aria-invalid'],
      'aria-disabled': own['aria-disabled'] ?? injected['aria-disabled'],
      id: own.id ?? injected.id,
      'aria-describedby': own['aria-describedby'] ?? injected['aria-describedby'],
      disabled: own.disabled ?? injected.disabled,
    });
  };
  return cloneElement(child, { render: wrapped } as Partial<RenderPropChild>);
}

export function FormField({
  label,
  name,
  error,
  helperText,
  required,
  className,
  hideLabel,
  disabled,
  disabledHint,
  locked,
  disabledHintReveal = 'always',
  helperTone = 'muted',
  children,
}: FormFieldProps): ReactElement {
  // An explicit prop wins; otherwise the schema-derived set decides. `??` not
  // `||` — `required={false}` is a deliberate opt-out and must beat the context
  // (company-standards.md "0 is a value").
  const derivedRequired = useContext(RequiredFieldsContext);
  const layout = useContext(FieldLayoutContext);
  // A phone sheet's stacked field takes the sheet's label and 14px floor (BL-2402).
  const isTouch = useContext(FieldSizeContext) === 'touch';
  const isRequired = required ?? derivedRequired?.has(name) ?? false;
  // BUG-2653: `name` stays the schema key; the DOM ids carry the form's scope so a
  // second instance of the form in the document gets ids of its own.
  const fieldKey = useScopedFieldId(name);

  const errorId = `${fieldKey}-error`;
  // The lock hint reuses the helper slot when the field is disabled and there's
  // no error to surface (error always wins the single sub-input line).
  const showsLockHint = !!disabled && !!disabledHint;
  const effectiveHelper = showsLockHint ? disabledHint : helperText;
  const helperId = effectiveHelper ? `${fieldKey}-helper` : undefined;
  // BL-2398: an on-attempt lock reason stays announced but visually hidden
  // until the operator presses or focuses the locked control.
  const attempt = useAttemptReveal({
    locked: disabledHintReveal === 'on-attempt' && showsLockHint,
    hintId: helperId,
    testId: name,
  });
  const hintHidden = disabledHintReveal === 'on-attempt' && showsLockHint && !attempt.revealed;
  const isCaution = helperTone === 'caution' && !showsLockHint;
  const describedBy =
    [error ? errorId : null, helperId].filter(Boolean).join(' ') || undefined;

  const labelId = `${fieldKey}-label`;
  const child = Children.only(children);
  const enhanced = isValidElement<InjectedInputProps & RenderPropChild>(child)
    ? injectFieldProps(child, {
        id: child.props.id ?? fieldKey,
        // Component children only — a raw `<input>` would render it as an
        // unknown HTML attribute and warn. See `isHostElement`.
        ...(isHostElement(child.type) ? {} : { invalid: !!error }),
        'aria-invalid': error ? true : undefined,
        'aria-describedby': describedBy,
        // Explicit boolean wins; `undefined` preserves the child's own disabled.
        disabled: disabled ?? child.props.disabled,
        'aria-disabled': disabled ? true : undefined,
      })
    : child;
  const controlId = (isValidElement<InjectedInputProps>(child) ? child.props.id : undefined) ?? fieldKey;
  // No label text means no label element, so there is nothing for a control to point at.
  const hasLabel = Boolean(label);
  const labelTarget = useMemo<FieldLabelTarget | null>(
    () => (hasLabel ? { labelId, controlId } : null),
    [hasLabel, labelId, controlId],
  );
  const control = (
    <FieldLabelTargetContext.Provider value={labelTarget}>{attempt.wrap(enhanced)}</FieldLabelTargetContext.Provider>
  );

  if (layout === 'row') {
    return (
      <FieldRow
        label={label}
        hideLabel={hideLabel}
        htmlFor={fieldKey}
        labelId={labelId}
        required={isRequired}
        locked={locked ?? disabled === true}
        className={className}
        below={
          <>
            {effectiveHelper && !error ? (
              <p
                id={helperId}
                data-testid={effectiveHelper ? `${name}-helper` : undefined}
                data-caution={isCaution ? 'true' : undefined}
                className={clsx('mt-1 text-caption', isCaution ? CAUTION_TEXT_CLASS : FIELD_HINT_COLOR, hintHidden && 'sr-only')}
              >
                {effectiveHelper}
              </p>
            ) : null}
            {error ? (
              <FieldErrorText id={errorId} className={FIELD_ERROR_CLASS.card}>
                {error}
              </FieldErrorText>
            ) : null}
          </>
        }
      >
        {control}
      </FieldRow>
    );
  }

  const stackedLabelClass = isTouch ? STACKED_LABEL_CLASS_TOUCH : FIELD_LABEL_CLASS;
  const stackedLabelParts = STACKED_LABEL_PARTS[isTouch ? 'touch' : 'card'];
  // BUG-2545 (rule #47, ADR 072 — Enforced Obligations): the stacked padlock
  // derives from the lock state, as the row's does, so no caller passes it per
  // layout. A bare `disabled` (no reason) keeps its look in both apps (BL-2759).
  const stackedLock = (locked ?? (disabled === true && disabledHint != null)) && !hideLabel;

  return (
    <div className={className}>
      {stackedLock ? (
        <div className={clsx('flex items-center gap-1.5', stackedLabelParts.margin)}>
          <FieldLabel label={label} htmlFor={fieldKey} id={labelId} required={isRequired} className={stackedLabelParts.look} markClassName={FIELD_MARK_CLASS} />
          <FieldLockIcon htmlFor={fieldKey} />
        </div>
      ) : (
        <FieldLabel
          label={label}
          htmlFor={fieldKey}
          id={labelId}
          required={isRequired}
          className={hideLabel ? 'sr-only' : stackedLabelClass}
          markClassName={FIELD_MARK_CLASS}
        />
      )}
      {control}
      {effectiveHelper && !error ? (
        <p
          id={helperId}
          data-testid={effectiveHelper ? `${name}-helper` : undefined}
          data-caution={isCaution ? 'true' : undefined}
          className={clsx('mt-1', isTouch ? 'text-body' : 'text-caption', isCaution ? CAUTION_TEXT_CLASS : FIELD_HINT_COLOR, hintHidden && 'sr-only')}
        >
          {effectiveHelper}
        </p>
      ) : null}
      {error ? (
        <FieldErrorText id={errorId} className={FIELD_ERROR_CLASS[isTouch ? 'touch' : 'card']}>
          {error}
        </FieldErrorText>
      ) : null}
    </div>
  );
}

export interface FieldRowProps {
  /** Visible label. Omit for a control that labels itself (the label cell stays, empty). */
  label?: string;
  /** The control's id, so the label is associated with it. */
  htmlFor?: string;
  /** The label's id — for a widget named by `aria-labelledby` rather than `for`. */
  labelId?: string;
  /** Keep the label in the accessibility tree but visually hidden (sr-only). */
  hideLabel?: boolean;
  /** Draw the required marker. */
  required?: boolean;
  /**
   * The control is locked (ADR 034 — Field Editability): a lock icon joins the
   * label. The caller's verdict decides this; the row only draws it.
   */
  locked?: boolean;
  /**
   * Names the field in the lock icon's automation handle (`<field>-lock-icon`) when there
   * is no `htmlFor` to derive it from (a widget of several buttons, BUG-2545).
   */
  field?: string;
  /** Hints / errors, rendered under the control in the control column. */
  below?: ReactNode;
  className?: string;
  /** The control (or read-only value) for this row. */
  children?: ReactNode;
}

/**
 * BL-1978 — one field row for a control `FormField` can't wrap (a widget that
 * owns several elements, a checkbox, a read-only value).
 *
 * Follows {@link FieldLayoutContext}: in `row` layout it is the 116px label
 * column beside the control, 36px tall on desktop and 44px on a phone; in
 * `stacked` it is the label above the control, as `FormField` draws it.
 *
 * @param props - {@link FieldRowProps}
 * @returns the labelled row
 */
export function FieldRow({
  label,
  htmlFor,
  labelId,
  hideLabel,
  required,
  locked,
  field,
  below,
  className,
  children,
}: FieldRowProps): ReactElement {
  const layout = useContext(FieldLayoutContext);
  const isTouch = useContext(FieldSizeContext) === 'touch';
  const compact = useContext(FieldRowColumnContext) === 'compact';
  if (layout === 'row') {
    const gridClass = isTouch ? (compact ? ROW_GRID_CLASS_TOUCH_COMPACT : ROW_GRID_CLASS_TOUCH) : ROW_GRID_CLASS;
    return (
      <div
        className={className ? `${gridClass} ${className}` : gridClass}
        data-field-row=""
        data-locked={locked ? 'true' : undefined}
      >
        <div className={isTouch ? ROW_LABEL_CLASS_TOUCH : ROW_LABEL_CLASS}>
          {label ? (
            // The label and its marker share one group, so the cell's gap
            // (which spaces the lock icon) doesn't push the "*" off its word.
            <span className="flex min-w-0 items-center">
              <FieldLabel
                label={label}
                htmlFor={htmlFor}
                id={labelId}
                // BL-1978 QA (AC 6): a tenant's custom-field label can run to 2–3
                // lines in the 116px column, stretching its row to 40–60px. Keep
                // it to one line; the full label shows on hover. The required
                // marker is drawn beside the label, not inside it, so the "…"
                // can't hide it (there is no hover on a phone).
                required={hideLabel ? required : false}
                className={hideLabel ? 'sr-only' : 'min-w-0 truncate'}
                title={hideLabel ? undefined : label}
              />
              {required && !hideLabel ? <RequiredMarker className={ROW_MARK_CLASS} /> : null}
            </span>
          ) : null}
          {locked ? <FieldLockIcon htmlFor={htmlFor} testField={field} /> : null}
        </div>
        <div className="min-w-0">
          <div className={isTouch ? ROW_CELL_CLASS_TOUCH : ROW_CELL_CLASS}>
            <div className="w-full min-w-0">{children}</div>
          </div>
          {below}
        </div>
      </div>
    );
  }
  // BUG-2545: a locked stacked row draws the padlock beside its label, as FormField's does.
  const stackedParts = STACKED_LABEL_PARTS[isTouch ? 'touch' : 'card'];
  return (
    <div className={className} data-locked={locked ? 'true' : undefined}>
      {locked && label && !hideLabel ? (
        <div className={clsx('flex items-center gap-1.5', stackedParts.margin)}>
          <FieldLabel label={label} htmlFor={htmlFor} id={labelId} required={required} className={stackedParts.look} markClassName={FIELD_MARK_CLASS} />
          <FieldLockIcon htmlFor={htmlFor} testField={field} />
        </div>
      ) : (
        <FieldLabel
          label={label}
          htmlFor={htmlFor}
          id={labelId}
          required={required}
          className={hideLabel ? 'sr-only' : isTouch ? STACKED_LABEL_CLASS_TOUCH : FIELD_LABEL_CLASS}
          markClassName={FIELD_MARK_CLASS}
        />
      )}
      {children}
      {below}
    </div>
  );
}
